import type { EditorElement } from "../../../editor/types";
import type { AudioStyle, ImageStyle, VideoStyle } from "../types";
import { createElementFromTemplate } from "./templates";

export type LocalCanvasMediaKind = "image" | "audio" | "video";

type FileDescriptor = Pick<File, "name" | "size" | "type">;

const MEDIA_KIND_BY_MIME_TYPE: Record<string, LocalCanvasMediaKind> = {
  "image/jpeg": "image",
  "image/png": "image",
  "image/webp": "image",
  "audio/mpeg": "audio",
  "audio/mp3": "audio",
  "video/mp4": "video",
};

const MEDIA_KIND_BY_EXTENSION: Record<string, LocalCanvasMediaKind> = {
  jpg: "image",
  jpeg: "image",
  png: "image",
  webp: "image",
  mp3: "audio",
  mp4: "video",
};

const getFileExtension = (fileName: string): string => {
  const extensionStart = fileName.lastIndexOf(".");
  return extensionStart >= 0 ? fileName.slice(extensionStart + 1).toLowerCase() : "";
};

const replaceFileExtension = (fileName: string, extension: string): string => {
  const extensionStart = fileName.lastIndexOf(".");
  const baseName = extensionStart >= 0 ? fileName.slice(0, extensionStart) : fileName;
  return `${baseName}.${extension}`;
};

// Any raster image we can decode into a canvas is convertible to WebP.
// WebP is already the target format, and SVG is vector (rasterizing it would
// lose scalability) — both are skipped.
const isConvertibleImageFile = (
  file: Pick<File, "name" | "type">
): boolean => {
  if (getLocalCanvasMediaKind(file) !== "image") {
    return false;
  }
  const type = file.type.toLowerCase();
  const extension = getFileExtension(file.name);
  if (type === "image/webp" || extension === "webp") {
    return false;
  }
  if (type === "image/svg+xml" || extension === "svg") {
    return false;
  }
  return true;
};

export const getLocalCanvasMediaKind = (
  file: Pick<File, "name" | "type">
): LocalCanvasMediaKind | null => {
  const mimeKind = MEDIA_KIND_BY_MIME_TYPE[file.type.toLowerCase()];
  if (mimeKind) {
    return mimeKind;
  }

  return MEDIA_KIND_BY_EXTENSION[getFileExtension(file.name)] || null;
};

export const convertImageFileToWebp = async (
  file: File,
  quality = 0.92
): Promise<File> => {
  if (!isConvertibleImageFile(file)) {
    return file;
  }

  const objectUrl = URL.createObjectURL(file);

  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error("Image could not be loaded."));
      img.src = objectUrl;
    });

    const canvas = document.createElement("canvas");
    canvas.width = image.naturalWidth || image.width;
    canvas.height = image.naturalHeight || image.height;

    const context = canvas.getContext("2d");
    if (!context) {
      throw new Error("Canvas context could not be created.");
    }

    context.drawImage(image, 0, 0);

    const blob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (result) => {
          if (result) {
            resolve(result);
          } else {
            reject(new Error("WebP conversion failed."));
          }
        },
        "image/webp",
        quality
      );
    });

    return new File([blob], replaceFileExtension(file.name, "webp"), {
      type: "image/webp",
      lastModified: file.lastModified,
    });
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
};

const setImageSource = (
  element: EditorElement,
  file: FileDescriptor,
  sourceUrl: string
): EditorElement => ({
  ...element,
  data: {
    ...element.data,
    style: {
      ...(element.data.style as Partial<ImageStyle>),
      url: sourceUrl,
      alt: file.name,
    },
  },
});

const setAudioSource = (
  element: EditorElement,
  file: FileDescriptor,
  sourceUrl: string
): EditorElement => ({
  ...element,
  size: { width: 64, height: 56 },
  data: {
    ...element.data,
    style: {
      ...(element.data.style as Partial<AudioStyle>),
      sourceUrl,
      label: file.name,
      showControls: false,
      showIcon: true,
      showLabel: false,
    },
  },
});

const setVideoSource = (
  element: EditorElement,
  file: FileDescriptor,
  sourceUrl: string
): EditorElement => ({
  ...element,
  data: {
    ...element.data,
    style: {
      ...(element.data.style as Partial<VideoStyle>),
      sourceUrl,
      label: file.name,
    },
  },
});

export const createLocalCanvasMediaElement = (
  file: FileDescriptor,
  sourceUrl: string
): EditorElement | null => {
  const mediaKind = getLocalCanvasMediaKind(file);
  const template =
    mediaKind === "image"
      ? "image-basic"
      : mediaKind === "audio"
        ? "audio-basic"
        : mediaKind === "video"
          ? "slide-basic"
          : null;

  if (!template) {
    return null;
  }

  const element = createElementFromTemplate(template);
  const elementWithSource =
    mediaKind === "image"
      ? setImageSource(element, file, sourceUrl)
      : mediaKind === "audio"
        ? setAudioSource(element, file, sourceUrl)
        : setVideoSource(element, file, sourceUrl);

  return {
    ...elementWithSource,
    data: {
      ...elementWithSource.data,
      sourceFile: {
        fileName: file.name,
        mimeType: file.type,
        size: file.size,
      },
    },
  };
};
